import React, { useEffect, useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getTemplateSections, trackTemplateUsage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Search, X, FileX, Loader2, Star, Check, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

interface Template {
  id: string;
  title: string;
  type: string;
  value: any;
  category?: string;
  is_favorite?: boolean;
}

interface Props {
  open: boolean;
  type: string;
  onClose: () => void;
  onApply: (template: Template, mode: "replace" | "append") => void;
}

type FilterType = "all" | "personal" | "clinic";

const TYPE_LABELS: Record<string, string> = {
  complaint: "Complaints",
  medicine: "Medicines",
  investigation: "Investigations",
  advice: "Advice",
  diagnosis: "Diagnosis",
  quick_notes: "Quick Notes",
};

const APPENDABLE_TYPES = new Set(["complaint", "medicine", "investigation"]);

function safeParseValue(raw: any): any {
  if (typeof raw === "string") {
    try { return JSON.parse(raw); } catch { return raw; }
  }
  return raw;
}

function getPreview(template: Template): string {
  if (!template.value) return "";
  try {
    const v = safeParseValue(template.value);
    if (template.type === "complaint" && Array.isArray(v))
      return v.filter((c: any) => c.complaint).map((c: any) => c.complaint).join(", ");
    if (template.type === "medicine" && Array.isArray(v))
      return v.filter((m: any) => m.brand_name || m.product).map((m: any) => m.brand_name || m.product).join(", ");
    if (template.type === "investigation" && Array.isArray(v))
      return v.join(", ");
    if (typeof v === "string") return v.length > 120 ? v.slice(0, 120) + "…" : v;
    if (typeof v?.value === "string") return v.value.length > 120 ? v.value.slice(0, 120) + "…" : v.value;
  } catch { /* ignore */ }
  return "";
}

export default function LoadTemplateModal({ open, type, onClose, onApply }: Props) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<FilterType>("all");
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!open) { setSearch(""); setFilter("all"); setTemplates([]); return; }
    (async () => {
      try {
        setLoading(true);
        const data = await getTemplateSections(
            type,
            currentUser?.doctor_id || "",
            currentUser?.clinic_id || "",
            { favorites_only: false, limit: 100 }
        );
        setTemplates(data?.templates ? data.templates : Array.isArray(data) ? data : []);
      } catch { setTemplates([]); }
      finally { setLoading(false); }
    })();
  }, [open, type, currentUser?.doctor_id, currentUser?.clinic_id]);

  const filtered = useMemo(() => {
    return templates.filter((t) => {
      if (search) {
        const q = search.toLowerCase();
        if (!t.title?.toLowerCase().includes(q) && !getPreview(t).toLowerCase().includes(q))
          return false;
      }
      if (filter === "personal") return t.category === "personal";
      if (filter === "clinic") return t.category === "clinic";
      return true;
    });
  }, [templates, search, filter]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      if (a.is_favorite && !b.is_favorite) return -1;
      if (!a.is_favorite && b.is_favorite) return 1;
      return (a.title || "").localeCompare(b.title || "", "en", { sensitivity: "base" });
    });
  }, [filtered]);

  const handleApply = (template: Template, mode: "replace" | "append") => {
    if (currentUser?.doctor_id)
      trackTemplateUsage(template.id, currentUser.doctor_id, "prescription_form").catch(() => {});
    onApply(template, mode);
    onClose();
  };

  const canAppend = APPENDABLE_TYPES.has(type);

  const emptyMessage =
      filter === "personal" ? "No personal templates yet" :
          filter === "clinic"   ? "No clinic templates yet" :
              search                ? `No results for "${search}"` :
                  "No templates saved yet";

  return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-lg p-0 overflow-hidden flex flex-col gap-0 max-h-[75vh]">

          {/* Header */}
          <DialogHeader className="px-5 py-4 border-b border-border flex-shrink-0">
            <DialogTitle className="text-sm font-semibold">
              Load {TYPE_LABELS[type] || "Template"}
            </DialogTitle>
          </DialogHeader>

          {/* Search + filter */}
          <div className="px-4 pt-3 pb-2.5 space-y-2 flex-shrink-0 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
              <Input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search…"
                  className="pl-8 pr-8 h-8 text-sm"
              />
              {search && (
                  <button
                      onClick={() => setSearch("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
              )}
            </div>

            <div className="flex gap-1">
              {(["all", "personal", "clinic"] as FilterType[]).map((f) => (
                  <button
                      key={f}
                      onClick={() => setFilter(f)}
                      className={cn(
                          "px-3 py-1 rounded-full text-xs font-medium transition-colors capitalize",
                          filter === f
                              ? "bg-primary text-primary-foreground"
                              : "text-muted-foreground hover:text-foreground hover:bg-muted"
                      )}
                  >
                    {f}
                  </button>
              ))}
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto px-4 py-2 space-y-1">
            {loading ? (
                <div className="flex items-center justify-center h-36">
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                </div>
            ) : sorted.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-36 gap-2 text-center">
                  <FileX className="w-7 h-7 text-muted-foreground/40" />
                  <p className="text-xs text-muted-foreground">{emptyMessage}</p>
                </div>
            ) : (
                sorted.map((template) => {
                  const preview = getPreview(template);

                  return (
                      <div
                          key={template.id}
                          className={cn(
                              "flex items-start gap-3 px-3 py-2.5 rounded-md border border-border",
                              "transition-colors hover:bg-muted/40"
                          )}
                      >
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            {!!template.is_favorite && (
                                <Star className="w-3 h-3 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                            )}
                            <span className="text-sm font-medium truncate">{template.title}</span>
                            {template.category === "clinic" && (
                                <span className="text-[10px] text-muted-foreground border border-border rounded px-1 flex-shrink-0">
                          clinic
                        </span>
                            )}
                          </div>
                          {preview && (
                              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{preview}</p>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {canAppend && (
                              <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleApply(template, "append")}
                                  className="h-7 px-2 text-xs gap-1"
                                  title="Add to existing"
                              >
                                <Plus className="w-3 h-3" />
                                Add
                              </Button>
                          )}
                          <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => handleApply(template, "replace")}
                              className="h-7 px-2 text-xs gap-1"
                              title="Replace existing"
                          >
                            <Check className="w-3 h-3" />
                            {canAppend ? "Replace" : "Apply"}
                          </Button>
                        </div>
                      </div>
                  );
                })
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-border flex items-center justify-between flex-shrink-0">
            <p className="text-xs text-muted-foreground">
              {sorted.length} template{sorted.length !== 1 ? "s" : ""}
            </p>
            <Button variant="ghost" size="sm" onClick={onClose} className="h-7 text-xs">
              Cancel
            </Button>
          </div>

        </DialogContent>
      </Dialog>
  );
}